import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "../locales/en";
import { APP_LOCALES, isAppLocale, localeFromCookieHeader, LOCALE_COOKIE, type AppLocale } from "./localePath";

export const SUPPORTED_LANGUAGES = APP_LOCALES;
type SupportedLanguage = AppLocale;

const STORAGE_KEY = LOCALE_COOKIE;

/**
 * English ships in the entry bundle (it is the fallback and what crawlers
 * see). Russian and Uzbek are ~5k lines each, so they load on demand the
 * first time the language becomes active, mirroring I18nAntdProvider.
 */
const LAZY_LANGUAGES: Record<string, () => Promise<{ default: object }>> = {
  ru: () => import("../locales/ru"),
  uz: () => import("../locales/uz"),
};

export const loadLanguage = async (lng: string | undefined): Promise<void> => {
  const base = lng?.split("-")[0];
  if (!base) return;
  const loader = LAZY_LANGUAGES[base];
  if (!loader || i18n.hasResourceBundle(base, "translation")) return;
  const { default: resources } = await loader();
  i18n.addResourceBundle(base, "translation", resources, true, true);
};

const isSupported = (lng: string | undefined): lng is SupportedLanguage =>
  isAppLocale(lng);

/**
 * The visitor's saved language, else the browser's, else English. Browser
 * only: the server always renders English, and the client switches after
 * hydration so the first client render matches the server HTML.
 */
export const detectPreferredLanguage = (): SupportedLanguage => {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isSupported(stored ?? undefined)) return stored as SupportedLanguage;
  } catch {
    // Storage can be blocked; fall through to the browser language.
  }
  const fromCookie = localeFromCookieHeader(window.document.cookie);
  if (fromCookie) return fromCookie;
  for (const candidate of window.navigator.languages ?? []) {
    const base = candidate.split("-")[0];
    if (isSupported(base)) return base;
  }
  return "en";
};

void i18n.use(initReactI18next).init({
  // Always start in English, on the server and for the hydrating render.
  lng: "en",
  resources: {
    en: { translation: en },
  },
  // ru/uz bundles are added later by loadLanguage.
  partialBundledLanguages: true,
  fallbackLng: "en",
  supportedLngs: [...SUPPORTED_LANGUAGES],
  interpolation: { escapeValue: false },
  // Initialize synchronously so the first render already has strings.
  initAsync: false,
  react: {
    useSuspense: false,
    // Re-render when a lazily loaded bundle arrives.
    bindI18nStore: "added",
  },
});

// Registered after init: init itself emits languageChanged("en"), which must
// not overwrite a visitor's saved choice before it has been read.
i18n.on("languageChanged", (lng) => {
  void loadLanguage(lng);
  if (typeof window === "undefined" || !isAppLocale(lng)) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    // Not persisting is fine; the choice still applies to this visit.
  }
  // The server reads this on the next unprefixed visit.
  document.cookie = `${LOCALE_COOKIE}=${lng}; Path=/; Max-Age=31536000; SameSite=Lax`;
});

export default i18n;
